import type { startseite } from "@progressive-development/politik-model";

/**
 * Die Ordnung der Sitzverteilung — als eigene Funktion, weil sie eine Regel
 * ist und keine Formatierung.
 *
 * ZWEI KRITERIEN, in dieser Reihenfolge:
 *
 * 1. Fraktionen zuerst, alles andere danach. Gruppen und Einzelmandate sind
 *    keine Fraktionen; zwischen sie einsortiert legten sie etwas anderes nahe.
 *    Die Quelle unterscheidet das (`status`), also unterscheidet die Anzeige es
 *    auch — nicht über Namen, nicht über Größe.
 * 2. Innerhalb beider Gruppen nach Sitzen absteigend.
 *
 * Beides muss in der Auswahl-Zeile der Kachel stehen, sonst liest sich die
 * Reihenfolge als Wertung.
 *
 * Bei Gleichstand bleibt die Reihenfolge der Quelle erhalten (stabile
 * Sortierung): Ein zweites Kriterium zu erfinden — alphabetisch etwa — hieße,
 * eine Rangfolge zu behaupten, die es nicht gibt.
 */
export function sortiereSitze(eintraege: startseite.SitzEintrag[]): startseite.SitzEintrag[] {
  return [...eintraege].sort((a, b) => {
    const rangA = a.status === "fraktion" ? 0 : 1;
    const rangB = b.status === "fraktion" ? 0 : 1;
    if (rangA !== rangB) return rangA - rangB;
    return b.sitze - a.sitze;
  });
}

/**
 * Die Mehrheitsschwelle: mehr als die Hälfte der Sitze.
 *
 * Arithmetik, keine Aussage — bei 90 Sitzen sind es 46, bei 91 ebenfalls 46.
 * Deshalb `floor(n / 2) + 1` und nicht `ceil(n / 2)`: Bei gerader Sitzzahl
 * reicht die Hälfte nicht, sie ist ein Patt.
 *
 * Zusammen mit der Gesamtzahl darüber ist damit ablesbar, ob eine Mehrheit
 * trägt — ohne dass jemand es behaupten müsste. WEITERE rechnerisch mögliche
 * Mehrheiten werden nicht gezeigt; das wäre Spekulation.
 */
export function mehrheitAb(sitzeGesamt: number): number {
  return Math.floor(sitzeGesamt / 2) + 1;
}

/**
 * Teilt die Sitz-Einträge in Bündnis und Übrige.
 *
 * Geteilt wird NUR bei `art: "buendnis"` mit benannten Beteiligten. Bei
 * wechselnden Mehrheiten gibt es nichts zu teilen — und bei fehlender Angabe
 * wüsste niemand, wonach. Beide Male steht eine Liste da; das ist kein zweiter
 * Bauweg, sondern derselbe Baustein einmal statt zweimal aufgerufen.
 *
 * Die Zuordnung läuft ausschließlich über den Partei-Slug — nie über Namen,
 * nie über Größe, nie über Nähe.
 */
export function teileNachMehrheit(
  eintraege: startseite.SitzEintrag[],
  mehrheitsbildung: startseite.Mehrheitsbildung | undefined,
): { buendnis: startseite.SitzEintrag[]; uebrige: startseite.SitzEintrag[] } | undefined {
  if (mehrheitsbildung?.art !== "buendnis" || !mehrheitsbildung.parteien?.length) {
    return undefined;
  }
  const dabei = new Set(mehrheitsbildung.parteien);
  const sortiert = sortiereSitze(eintraege);
  return {
    buendnis: sortiert.filter((e) => dabei.has(e.partei)),
    uebrige: sortiert.filter((e) => !dabei.has(e.partei)),
  };
}

/**
 * Summe der Sitze einer Spalte — Arithmetik, keine Aussage.
 *
 * Für die Bündnis-Spalte gilt: Liefert die Quelle `sitze`, wird DIESE Zahl
 * gezeigt und nicht nachgerechnet — so kann sie mit der Sitztabelle daneben
 * nicht auseinanderlaufen.
 */
export function summeSitze(eintraege: startseite.SitzEintrag[]): number {
  return eintraege.reduce((summe, e) => summe + e.sitze, 0);
}
